(() => {
  const WIDGET_ID = "279224";
  const ROOT_SELECTOR = `#rebuy-widget-${WIDGET_ID}`;
  const READY_CLASS = "rbfbt-ready";
  const PENDING_CLASS = "rbfbt-pending";
  const SKELETON_CLASS = "rbfbt-skeleton";
  const COLOR_KEYWORDS = ["black", "white", "cream", "brown", "grey", "gray", "beige", "nude", "tan", "navy", "blue", "green", "red", "pink", "yellow", "orange", "purple", "gold", "silver", "lemons", "lemon"];
  const MAX_PRODUCTS = 3;
  const FAMILY_MATCHERS = [
    { key: "duffle", patterns: [/duffle/i, /duffel/i, /weekend bag/i] },
    { key: "garment", patterns: [/dress cover/i, /garment/i] },
    { key: "luggage", patterns: [/luggage/i, /suitcase/i, /travel suitcase/i, /travel bag/i] },
    { key: "perfume", patterns: [/perfume bag/i, /perfume pouch/i] },
    { key: "makeup", patterns: [/makeup bag/i, /cosmetic bag/i, /toiletry bag/i] }
  ];
  const CORE_BUNDLE_ROLES = ["luggage", "duffle", "garment", "small-bag"];
  const BUNDLE_ROLE_REQUIREMENTS = {
    luggage: ["duffle", "garment", "small-bag"],
    duffle: ["luggage", "garment", "small-bag"],
    garment: ["luggage", "duffle", "small-bag"],
    "small-bag": ["luggage", "duffle", "garment"]
  };
  const ROLE_FAMILY_PREFERENCE = {
    luggage: ["luggage"],
    duffle: ["duffle"],
    garment: ["garment"],
    "small-bag": ["perfume", "makeup"]
  };
  const FAMILY_SEARCH_TERMS = {
    luggage: ["luggage", "travel luggage", "suitcase"],
    duffle: ["duffle bag", "duffel bag", "weekend bag", "travel bag"],
    garment: ["dress cover", "garment bag"],
    perfume: ["perfume bag", "perfume pouch"],
    makeup: ["makeup bag", "cosmetic bag", "toiletry bag"]
  };

  let bootObserver = null;
  let rootObserver = null;
  let renderTimer = null;
  let boundRoot = null;
  let visibilityObserver = null;
  let isRendering = false;
  let lastKnownCartCount = null;
  let preferredBundleColor = "";
  let bundleProductsPromise = null;
  let bundleProductsHandle = "";
  const productDataCache = new Map();
  const searchResultsCache = new Map();

  function getRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function rootNeedsRender(root) {
    if (!root) {
      return false;
    }

    if (!root.classList.contains(READY_CLASS)) {
      return true;
    }

    return !root.querySelector(".rbfbt-card-ready");
  }

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function normalizeUrl(value) {
    if (!value) {
      return "";
    }

    try {
      return new URL(value, window.location.origin).pathname.replace(/\/+$/, "");
    } catch (_error) {
      return String(value).replace(window.location.origin, "").replace(/\/+$/, "");
    }
  }

  function getProductHandleFromUrl(value) {
    const path = normalizeUrl(value);
    const match = path.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : "";
  }

  function getCurrentProductHandle() {
    return window.meta?.product?.handle || getProductHandleFromUrl(document.querySelector('link[rel="canonical"]')?.href || window.location.pathname);
  }

  function flattenValues(values) {
    const flattened = [];

    values.forEach((value) => {
      if (Array.isArray(value)) {
        flattened.push(...flattenValues(value));
      } else if (value != null && value !== "") {
        flattened.push(String(value));
      }
    });

    return flattened;
  }

  function extractColorValue(values, preferredColor = "") {
    const sources = flattenValues(values).map((value) => value.replace(/'/g, "").toLowerCase());

    if (preferredColor) {
      const matchedPreferred = COLOR_KEYWORDS.find((keyword) => keyword === preferredColor && sources.some((source) => source.includes(keyword)));

      if (matchedPreferred) {
        return matchedPreferred;
      }
    }

    for (const source of sources) {
      for (const keyword of COLOR_KEYWORDS) {
        if (source.includes(keyword)) {
          return keyword;
        }
      }
    }

    return "";
  }

  function getProductColor(product, preferredColor = "") {
    const optionValues = Array.isArray(product?.options) ? product.options.map((option) => {
      if (typeof option === "string") {
        return option;
      }

      return [option?.name, option?.values];
    }) : [];

    const variantValues = Array.isArray(product?.variants) ? product.variants.map((variant) => [variant?.title, variant?.public_title, variant?.option1, variant?.option2, variant?.option3, variant?.name]) : [];

    return extractColorValue([product?.title, optionValues, variantValues], preferredColor);
  }

  function getVariantColor(variant, preferredColor = "") {
    return extractColorValue([variant?.title, variant?.public_title, variant?.option1, variant?.option2, variant?.option3, variant?.name], preferredColor);
  }

  function getProductImage(product) {
    if (typeof product?.featured_image === "string") {
      return product.featured_image;
    }

    if (product?.featured_image?.url) {
      return product.featured_image.url;
    }

    if (product?.featured_image?.src) {
      return product.featured_image.src;
    }

    if (typeof product?.image === "string") {
      return product.image;
    }

    if (Array.isArray(product?.images) && product.images[0]) {
      if (typeof product.images[0] === "string") {
        return product.images[0];
      }

      if (product.images[0]?.src) {
        return product.images[0].src;
      }
    }

    return "";
  }

  function getSizedProductImage(product, width = 280) {
    const image = getProductImage(product);

    if (!image) {
      return "";
    }

    try {
      const url = new URL(image, window.location.origin);
      url.searchParams.set("width", String(width));
      return url.toString();
    } catch (_error) {
      const separator = image.includes("?") ? "&" : "?";
      return `${image}${separator}width=${width}`;
    }
  }

  function escapeHtml(value) {
    return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  function normalizeText(value) {
    return String(value ?? "").toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  }

  function normalizeProductTitle(value) {
    return normalizeText(value).replace(/\b(dhs|aed)\b/g, "").trim();
  }

  function getProductTitleHandleText(product) {
    return normalizeText([
      product?.title,
      product?.handle
    ].join(" "));
  }

  function getProductFamilyKeys(product) {
    const haystack = getProductTitleHandleText(product);

    return FAMILY_MATCHERS.filter((family) => {
      return family.patterns.some((pattern) => pattern.test(haystack));
    }).map((family) => family.key);
  }

  function matchesProductFamily(product, familyKey) {
    return getProductFamilyKeys(product).includes(familyKey);
  }

  function getProductBundleRole(product) {
    const familyKeys = getProductFamilyKeys(product);

    if (familyKeys.includes("perfume") || familyKeys.includes("makeup")) {
      return "small-bag";
    }

    if (familyKeys.includes("duffle")) {
      return "duffle";
    }

    if (familyKeys.includes("garment")) {
      return "garment";
    }

    if (familyKeys.includes("luggage")) {
      return "luggage";
    }

    return "";
  }

  function isEligibleFamilyProduct(product) {
    return CORE_BUNDLE_ROLES.includes(getProductBundleRole(product));
  }

  function normalizeMoneyToCents(value) {
    if (value == null || value === "") {
      return 0;
    }

    if (typeof value === "number") {
      return Number.isInteger(value) && value >= 1000 ? value : Math.round(value * 100);
    }

    const normalized = String(value).replace(/[^0-9.]/g, "");
    if (!normalized) {
      return 0;
    }

    const numeric = Number(normalized);
    if (!Number.isFinite(numeric)) {
      return 0;
    }

    return normalized.includes(".") ? Math.round(numeric * 100) : (numeric >= 1000 ? numeric : Math.round(numeric * 100));
  }

  function formatMoney(value) {
    return `Dhs. ${(normalizeMoneyToCents(value) / 100).toFixed(2)}`;
  }

  function getAvailableVariants(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants.filter((variant) => variant?.available);
  }

  function isProductAvailable(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];

    if (variants.length === 0) {
      return product?.available !== false;
    }

    return variants.some((variant) => variant?.available !== false);
  }

  function getDefaultVariantId(product, isCurrentProduct, preferredColor = "") {
    if (isCurrentProduct) {
      const currentId = Number(document.querySelector('product-form form [name="id"]')?.value || 0);
      if (currentId) {
        return currentId;
      }
    }

    const variants = getAvailableVariants(product);
    const preferredVariant = preferredColor ? variants.find((variant) => getVariantColor(variant, preferredColor) === preferredColor) : null;
    if (preferredVariant?.id) {
      return Number(preferredVariant.id);
    }

    return Number((variants[0] || product?.variants?.[0] || {}).id || 0);
  }

  function buildVariantOptions(product, selectedVariantId) {
    return (Array.isArray(product?.variants) ? product.variants : []).filter((variant) => variant?.available).map((variant) => {
      const title = variant.public_title || variant.title || "Default";
      return `<option value="${escapeHtml(variant.id)}"${Number(variant.id) === Number(selectedVariantId) ? " selected" : ""}>${escapeHtml(title)}</option>`;
    }).join("");
  }

  async function fetchProductDataByHandle(handle) {
    if (!handle) {
      return null;
    }

    if (!productDataCache.has(handle)) {
      productDataCache.set(handle, fetch(`${Shopify.routes.root}products/${handle}.js`, {
        credentials: "same-origin"
      }).then((response) => response.ok ? response.json() : null).catch(() => null));
    }

    return productDataCache.get(handle);
  }

  async function fetchRecommendations(productId) {
    const response = await fetch(`${Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=12&intent=related`, {
      credentials: "same-origin"
    });
    const payload = await response.json();
    return Array.isArray(payload?.products) ? payload.products : [];
  }

  async function fetchSearchProducts(query) {
    const normalizedQuery = normalizeText(query);

    if (!normalizedQuery) {
      return [];
    }

    if (!searchResultsCache.has(normalizedQuery)) {
      const params = new URLSearchParams({
        q: normalizedQuery,
        "resources[type]": "product",
        "resources[limit]": "10",
        "resources[options][unavailable_products]": "hide",
        "resources[options][fields]": "title,product_type,variants.title,tag"
      });

      searchResultsCache.set(normalizedQuery, fetch(`${Shopify.routes.root}search/suggest.json?${params.toString()}`, {
        credentials: "same-origin"
      }).then(async (response) => {
        if (!response.ok) {
          return [];
        }

        const payload = await response.json();
        return Array.isArray(payload?.resources?.results?.products) ? payload.resources.results.products : [];
      }).catch(() => []));
    }

    return searchResultsCache.get(normalizedQuery);
  }

  async function fetchCart() {
    const response = await fetch(`${Shopify.routes.root}cart.js?_rbfbt=${Date.now()}`, {
      credentials: "same-origin"
    });
    return response.json();
  }

  async function fetchFreshCart() {
    let cart = null;

    for (let attempt = 0; attempt < 4; attempt += 1) {
      cart = await fetchCart();

      if (cart?.item_count >= 0) {
        if (attempt === 0) {
          await wait(120);
          continue;
        }

        return cart;
      }

      await wait(150);
    }

    return cart;
  }

  async function dispatchCartChange() {
    try {
      const bundledSections = [];

      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: bundledSections }
      }));

      const cartContent = await fetchFreshCart();
      cartContent.sections = cartContent.sections || {};

      await Promise.all(bundledSections.map(async (sectionId) => {
        cartContent.sections[sectionId] = await (await fetch(`${Shopify.routes.root}?section_id=${sectionId}&_rbfbt=${Date.now()}`, {
          credentials: "same-origin"
        })).text();
      }));

      lastKnownCartCount = cartContent.item_count;

      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          onSuccessDo: "force_open_drawer",
          cart: cartContent
        }
      }));
    } catch (_error) {
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }
  }

  async function primeCartCount() {
    if (lastKnownCartCount !== null) {
      return;
    }

    try {
      lastKnownCartCount = (await fetchCart()).item_count;
    } catch (_error) {
      lastKnownCartCount = 0;
    }
  }

  function getSelectedItems(root) {
    return [...root.querySelectorAll(".rebuy-product-block")].filter((card) => {
      return card.querySelector(".rebuy-checkbox")?.checked;
    }).map((card) => {
      const productId = Number(card.dataset.productId || 0);
      const variantSelect = card.querySelector(".rebuy-select");
      const variantId = Number(variantSelect?.value || 0);
      const product = root._rbfbtProducts?.find((candidate) => Number(candidate.id) === productId);

      return {
        card,
        product,
        variantId
      };
    }).filter((item) => item.variantId && item.product);
  }

  function updateTotals(root) {
    const selectedItems = getSelectedItems(root);
    const total = selectedItems.reduce((sum, item) => {
      const variant = item.product.variants.find((candidate) => Number(candidate.id) === item.variantId);
      return sum + normalizeMoneyToCents(variant?.price || 0);
    }, 0);

    const totalValue = root.querySelector(".rebuy-bundle__actions-price-value .rebuy-money");
    const addButton = root.querySelector(".rebuy-bundle__actions .rebuy-button");

    if (totalValue) {
      totalValue.textContent = formatMoney(total);
    }

    if (addButton) {
      addButton.disabled = selectedItems.length === 0;
      addButton.setAttribute("aria-disabled", selectedItems.length === 0 ? "true" : "false");
    }
  }

  function syncCardSelection(card) {
    const checkbox = card?.querySelector(".rebuy-checkbox");
    const toggleButton = card?.querySelector(".rebuy-checkbox-label");
    const text = card?.querySelector(".rbfbt-toggle-text");
    const indicator = card?.querySelector(".rbfbt-toggle-indicator");
    const isSelected = Boolean(checkbox?.checked);

    card?.classList.toggle("rbfbt-is-selected", isSelected);
    card?.classList.toggle("rbfbt-is-unselected", !isSelected);
    toggleButton?.classList.toggle("is-selected", isSelected);
    toggleButton?.setAttribute("aria-pressed", isSelected ? "true" : "false");

    if (text) {
      text.textContent = isSelected ? "Included" : "Include";
    }

    if (indicator) {
      indicator.textContent = isSelected ? "✓" : "";
    }
  }

  function positionPlusMarkers(root) {
    const cards = [...root.querySelectorAll(".rebuy-product-block")];

    cards.forEach((card, index) => {
      const plus = card.querySelector(".rbfbt-plus");
      const media = card.querySelector(".rbfbt-media");
      const nextCard = cards[index + 1];

      if (!plus || !media) {
        return;
      }

      if (nextCard) {
        plus.style.display = "";
        const cardRect = card.getBoundingClientRect();
        const mediaRect = media.getBoundingClientRect();
        plus.style.top = `${mediaRect.top - cardRect.top + mediaRect.height / 2}px`;
      } else {
        plus.style.display = "none";
      }
    });
  }

  function buildProductCard(product, index, totalCount, selectedVariantId) {
    const productUrl = `${Shopify.routes.root}products/${product.handle}`;
    const imageUrl = getSizedProductImage(product);
    const title = product.title || "";
    const price = normalizeMoneyToCents(product.variants.find((variant) => Number(variant.id) === Number(selectedVariantId))?.price || product.price || 0);

    return `<div class="rebuy-product-block is-selected rbfbt-card-ready rbfbt-is-selected" data-product-id="${escapeHtml(product.id)}" data-product-handle="${escapeHtml(product.handle)}">
      <a class="rbfbt-media" href="${escapeHtml(productUrl)}" rel="nofollow">
        <div class="rebuy-product-checkbox">
          <input type="checkbox" class="checkbox-input rebuy-checkbox" checked tabindex="-1" aria-hidden="true">
          <button type="button" class="rebuy-checkbox-label is-selected" aria-pressed="true">
            <span class="sr-only">Select ${escapeHtml(title)} for bundle</span>
            <span class="rbfbt-toggle-indicator" aria-hidden="true">✓</span>
            <span class="rbfbt-toggle-text">Included</span>
          </button>
        </div>
        ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(title)}" loading="lazy" decoding="async">` : ""}
      </a>
      ${index < totalCount - 1 ? '<span class="rbfbt-plus" aria-hidden="true">+</span>' : ""}
      <div class="rebuy-product-info">
        <h5 class="rebuy-product-title">
          <a href="${escapeHtml(productUrl)}" class="rebuy-product-title-link" rel="nofollow">${escapeHtml(title)}</a>
        </h5>
        <div class="rebuy-product-price">
          <div><span class="rebuy-money">${formatMoney(price)}</span></div>
        </div>
      </div>
      <div class="rebuy-product-options">
        <select class="rebuy-select" aria-label="variant of ${escapeHtml(title)}">
          ${buildVariantOptions(product, selectedVariantId)}
        </select>
      </div>
    </div>`;
  }

  function buildSkeletonCard(index, totalCount) {
    return `<div class="rebuy-product-block rbfbt-skeleton-card" aria-hidden="true">
      <div class="rbfbt-media rbfbt-skeleton-box">
        <div class="rebuy-product-checkbox">
          <div class="rebuy-checkbox-label rbfbt-skeleton-box rbfbt-skeleton-button"></div>
        </div>
      </div>
      ${index < totalCount - 1 ? '<span class="rbfbt-plus" aria-hidden="true">+</span>' : ""}
      <div class="rebuy-product-info">
        <div class="rebuy-product-title rbfbt-skeleton-copy">
          <span class="rbfbt-skeleton-line rbfbt-skeleton-line--title"></span>
          <span class="rbfbt-skeleton-line rbfbt-skeleton-line--title-short"></span>
        </div>
        <div class="rebuy-product-price">
          <span class="rbfbt-skeleton-line rbfbt-skeleton-line--price"></span>
        </div>
      </div>
      <div class="rebuy-product-options">
        <div class="rebuy-select rbfbt-skeleton-box rbfbt-skeleton-select"></div>
      </div>
    </div>`;
  }

  function renderSkeleton(root) {
    const itemsMarkup = Array.from({ length: MAX_PRODUCTS }, (_value, index) => {
      return buildSkeletonCard(index, MAX_PRODUCTS);
    }).join("");

    root._rbfbtProducts = [];
    root.classList.add(READY_CLASS, PENDING_CLASS, SKELETON_CLASS);
    root.innerHTML = `<div class="rebuy-widget-content">
      <h3 class="primary-title">Frequently Bought Together</h3>
      <div class="rebuy-bundle">
        <div class="rbfbt-carousel-shell">
          <button type="button" class="rbfbt-carousel-arrow rbfbt-carousel-arrow--prev" aria-label="Previous products">
            <span aria-hidden="true">â€¹</span>
          </button>
          <div class="rebuy-bundle__items">${itemsMarkup}</div>
          <button type="button" class="rbfbt-carousel-arrow rbfbt-carousel-arrow--next" aria-label="Next products">
            <span aria-hidden="true">â€º</span>
          </button>
        </div>
        <div class="rebuy-bundle__actions">
          <div class="rebuy-bundle__actions-price">
            <span class="rebuy-bundle__actions-price-label">Total Price:</span>
            <span class="rebuy-bundle__actions-price-value"><span class="rebuy-money">${formatMoney(0)}</span></span>
          </div>
          <div class="rebuy-bundle__actions-buttons">
            <button type="button" class="rebuy-button" disabled aria-disabled="true">Add to Cart</button>
          </div>
        </div>
      </div>
    </div>`;

    positionPlusMarkers(root);
    updateCarouselState(root);
  }

  function renderWidget(root, products) {
    const itemsMarkup = products.map((product, index) => {
      return buildProductCard(product, index, products.length, getDefaultVariantId(product, false, preferredBundleColor || getProductColor(product)));
    }).join("");

    root._rbfbtProducts = products;
    root.classList.remove(PENDING_CLASS, SKELETON_CLASS);
    root.classList.add(READY_CLASS);
    root.innerHTML = `<div class="rebuy-widget-content">
      <h3 class="primary-title">Frequently Bought Together</h3>
      <div class="rebuy-bundle">
        <div class="rbfbt-carousel-shell">
          <button type="button" class="rbfbt-carousel-arrow rbfbt-carousel-arrow--prev" aria-label="Previous products">
            <span aria-hidden="true">‹</span>
          </button>
          <div class="rebuy-bundle__items">${itemsMarkup}</div>
          <button type="button" class="rbfbt-carousel-arrow rbfbt-carousel-arrow--next" aria-label="Next products">
            <span aria-hidden="true">›</span>
          </button>
        </div>
        <div class="rebuy-bundle__actions">
          <div class="rebuy-bundle__actions-price">
            <span class="rebuy-bundle__actions-price-label">Total Price:</span>
            <span class="rebuy-bundle__actions-price-value"><span class="rebuy-money">${formatMoney(0)}</span></span>
          </div>
          <div class="rebuy-bundle__actions-buttons">
            <button type="button" class="rebuy-button">Add to Cart</button>
          </div>
        </div>
      </div>
    </div>`;

    root.querySelectorAll(".rebuy-product-block").forEach((card) => {
      const checkbox = card.querySelector(".rebuy-checkbox");

      checkbox?.addEventListener("change", () => {
        syncCardSelection(card);
        updateTotals(root);
      });

      syncCardSelection(card);
    });

    root.querySelectorAll(".rebuy-select").forEach((select) => {
      select.addEventListener("change", () => updateTotals(root));
    });

    updateTotals(root);
    positionPlusMarkers(root);
    updateCarouselState(root);
  }

  function scrollCarousel(root, direction) {
    const track = root?.querySelector(".rebuy-bundle__items");

    if (!track) {
      return;
    }

    const firstCard = track.querySelector(".rebuy-product-block");
    const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || "0") || 0;
    const delta = (firstCard?.getBoundingClientRect().width || track.clientWidth * 0.9) + gap;

    track.scrollBy({
      left: direction * delta,
      behavior: "smooth"
    });
  }

  function updateCarouselState(root) {
    const track = root?.querySelector(".rebuy-bundle__items");
    const prevButton = root?.querySelector(".rbfbt-carousel-arrow--prev");
    const nextButton = root?.querySelector(".rbfbt-carousel-arrow--next");

    if (!track || !prevButton || !nextButton) {
      return;
    }

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const current = Math.round(track.scrollLeft);

    prevButton.disabled = current <= 4;
    nextButton.disabled = current >= maxScrollLeft - 4;
  }

  function getRequiredBundleRoles(currentProduct) {
    const currentRole = getProductBundleRole(currentProduct);

    if (currentRole && BUNDLE_ROLE_REQUIREMENTS[currentRole]) {
      return BUNDLE_ROLE_REQUIREMENTS[currentRole];
    }

    return CORE_BUNDLE_ROLES.filter((role) => role !== currentRole).slice(0, MAX_PRODUCTS);
  }

  function isSameColorProduct(product, currentColor) {
    if (!currentColor) {
      return true;
    }

    return getProductColor(product, currentColor) === currentColor;
  }

  function findRecommendationCandidate(products, familyKey, currentColor, excludedHandles, excludedTitles, currentProductId, requireSameColor = true) {
    return products.find((product) => {
      if (!product?.handle || Number(product.id) === Number(currentProductId)) {
        return false;
      }

      const normalizedTitle = normalizeProductTitle(product.title);

      if (excludedHandles.has(product.handle) || excludedTitles.has(normalizedTitle)) {
        return false;
      }

      if (!matchesProductFamily(product, familyKey)) {
        return false;
      }

      if (requireSameColor && !isSameColorProduct(product, currentColor)) {
        return false;
      }

      return isProductAvailable(product);
    }) || null;
  }

  async function findSearchCandidate(familyKey, currentColor, excludedHandles, excludedTitles, currentProductId, requireSameColor = true) {
    const queries = FAMILY_SEARCH_TERMS[familyKey] || [];

    for (const term of queries) {
      const searchQuery = requireSameColor ? [currentColor, term].filter(Boolean).join(" ") : term;
      const products = await fetchSearchProducts(searchQuery);
      const candidate = products.find((product) => {
        if (!product?.handle || Number(product.id) === Number(currentProductId)) {
          return false;
        }

        const normalizedTitle = normalizeProductTitle(product.title);

        if (excludedHandles.has(product.handle) || excludedTitles.has(normalizedTitle)) {
          return false;
        }

        if (!matchesProductFamily(product, familyKey)) {
          return false;
        }

        if (requireSameColor && !isSameColorProduct(product, currentColor)) {
          return false;
        }

        return isProductAvailable(product);
      });

      if (candidate) {
        return candidate;
      }
    }

    return null;
  }

  async function resolveRoleCandidate(role, recommendationCandidates, currentColor, excludedHandles, excludedTitles, currentProductId) {
    const families = ROLE_FAMILY_PREFERENCE[role] || [];

    for (const requireSameColor of [true, false]) {
      for (const familyKey of families) {
        const recommendationCandidate = findRecommendationCandidate(
          recommendationCandidates,
          familyKey,
          currentColor,
          excludedHandles,
          excludedTitles,
          currentProductId,
          requireSameColor
        );

        if (recommendationCandidate) {
          return recommendationCandidate;
        }

        const searchCandidate = await findSearchCandidate(
          familyKey,
          currentColor,
          excludedHandles,
          excludedTitles,
          currentProductId,
          requireSameColor
        );

        if (searchCandidate) {
          return searchCandidate;
        }
      }
    }

    return null;
  }

  function warmSearchCache(requiredRoles, currentColor) {
    const queries = requiredRoles.flatMap((role) => {
      return (ROLE_FAMILY_PREFERENCE[role] || []).flatMap((familyKey) => {
        return (FAMILY_SEARCH_TERMS[familyKey] || []).map((term) => {
          return [currentColor, term].filter(Boolean).join(" ");
        });
      });
    });

    [...new Set(queries.filter(Boolean))].forEach((query) => {
      void fetchSearchProducts(query);
    });
  }

  async function getSameColorProducts() {
    const currentHandle = getCurrentProductHandle();
    const currentProduct = await fetchProductDataByHandle(currentHandle);

    if (!currentProduct?.id) {
      return [];
    }

    const currentColor = getProductColor(currentProduct);
    preferredBundleColor = currentColor;
    const recommendations = await fetchRecommendations(currentProduct.id);
    const selectedHandles = new Set([currentHandle]);
    const selectedTitles = new Set([normalizeProductTitle(currentProduct.title)]);
    const selectedProducts = [];

    const pushProduct = (product) => {
      if (!product || selectedProducts.length >= MAX_PRODUCTS) {
        return false;
      }

      const normalizedTitle = normalizeProductTitle(product.title);

      if (selectedHandles.has(product.handle) || selectedTitles.has(normalizedTitle)) {
        return false;
      }

      selectedHandles.add(product.handle);
      selectedTitles.add(normalizedTitle);
      selectedProducts.push(product);
      return true;
    };

    const recommendationCandidates = recommendations.filter((product) => {
      if (!isProductAvailable(product) || !product?.handle || Number(product.id) === Number(currentProduct.id)) {
        return false;
      }

      return isEligibleFamilyProduct(product);
    });
    const requiredRoles = getRequiredBundleRoles(currentProduct);
    warmSearchCache(requiredRoles, currentColor);

    for (const role of requiredRoles) {
      if (selectedProducts.length >= MAX_PRODUCTS) {
        break;
      }

      const candidate = await resolveRoleCandidate(
        role,
        recommendationCandidates,
        currentColor,
        selectedHandles,
        selectedTitles,
        currentProduct.id
      );

      pushProduct(candidate);
    }

    const hydratedProducts = await Promise.all(selectedProducts.slice(0, MAX_PRODUCTS).map(async (product) => {
      return (await fetchProductDataByHandle(product.handle)) || product;
    }));

    return hydratedProducts.slice(0, MAX_PRODUCTS);
  }

  function getBundleProductsPromise() {
    const currentHandle = getCurrentProductHandle();

    if (!currentHandle) {
      return Promise.resolve([]);
    }

    if (bundleProductsPromise && bundleProductsHandle === currentHandle) {
      return bundleProductsPromise;
    }

    bundleProductsHandle = currentHandle;
    bundleProductsPromise = getSameColorProducts().catch(() => []);

    return bundleProductsPromise;
  }

  function primeBundleProducts() {
    void getBundleProductsPromise();
  }

  async function addBundleToCart(root) {
    const items = getSelectedItems(root).map((item) => ({ id: item.variantId, quantity: 1 }));

    if (items.length === 0) {
      return;
    }

    const button = root.querySelector(".rebuy-bundle__actions .rebuy-button");
    button?.setAttribute("aria-busy", "true");

    try {
      await primeCartCount();

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          items,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error("Bundle add failed");
      }

      await dispatchCartChange();
    } catch (_error) {
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    } finally {
      button?.removeAttribute("aria-busy");
    }
  }

  function bindRootEvents(root) {
    root.addEventListener("click", (event) => {
      const actionButton = event.target.closest(".rebuy-bundle__actions .rebuy-button");
      if (actionButton && root.contains(actionButton)) {
        event.preventDefault();
        void addBundleToCart(root);
        return;
      }

      const carouselArrow = event.target.closest(".rbfbt-carousel-arrow");
      if (carouselArrow && root.contains(carouselArrow)) {
        event.preventDefault();
        scrollCarousel(root, carouselArrow.classList.contains("rbfbt-carousel-arrow--prev") ? -1 : 1);
        return;
      }

      const label = event.target.closest(".rebuy-checkbox-label");
      if (label && root.contains(label)) {
        event.preventDefault();
        event.stopPropagation();

        const card = label.closest(".rebuy-product-block");
        const checkbox = card?.querySelector(".rebuy-checkbox");

        if (!card || !checkbox) {
          return;
        }

        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    window.addEventListener("resize", () => {
      window.requestAnimationFrame(() => {
        positionPlusMarkers(root);
        updateCarouselState(root);
      });
    });

    const track = root.querySelector(".rebuy-bundle__items");
    track?.addEventListener("scroll", () => updateCarouselState(root), { passive: true });

    const mainVariantInput = document.querySelector('product-form form [name="id"]');
    mainVariantInput?.addEventListener("change", () => {
      const currentHandle = getCurrentProductHandle();
      const currentProduct = root._rbfbtProducts?.find((product) => product.handle === currentHandle);
      const currentProductId = currentProduct?.id;

      if (!currentProductId) {
        return;
      }

      const select = root.querySelector(`.rebuy-product-block[data-product-id="${currentProductId}"] .rebuy-select`);
      if (select) {
        select.value = String(mainVariantInput.value);
        updateTotals(root);
      }
    });
  }

  async function renderCustomWidget(root) {
    if (!root || isRendering) {
      return;
    }

    isRendering = true;

    try {
      renderSkeleton(root);
      const products = await getBundleProductsPromise();

      if (products.length === 0) {
        root._rbfbtProducts = [];
        root.classList.remove(PENDING_CLASS, READY_CLASS, SKELETON_CLASS);
        root.innerHTML = "";
        return;
      }

      renderWidget(root, products);
      bindRootEvents(root);
    } finally {
      isRendering = false;
    }
  }

  function scheduleRender() {
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(() => {
      const root = getRoot();
      if (root && rootNeedsRender(root)) {
        root.classList.add(PENDING_CLASS);
        void renderCustomWidget(root);
      }
    }, 30);
  }

  function observeRootContent(root) {
    if (rootObserver) {
      rootObserver.disconnect();
      rootObserver = null;
    }

    if (!root) {
      return;
    }

    rootObserver = new MutationObserver(() => {
      if (!isRendering && rootNeedsRender(root)) {
        scheduleRender();
      }
    });

    rootObserver.observe(root, {
      childList: true,
      subtree: true
    });
  }

  function watchForRoot() {
    if (bootObserver) {
      return;
    }

    bootObserver = new MutationObserver(() => {
      const root = getRoot();

      if (!root) {
        return;
      }

      if (root !== boundRoot) {
        boundRoot = root;
        observeRootContent(root);
        scheduleRender();
        return;
      }

      if (!isRendering && rootNeedsRender(root)) {
        scheduleRender();
      }
    });

    bootObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function startRenderWhenVisible(root) {
    if (!root) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      scheduleRender();
      return;
    }

    visibilityObserver?.disconnect();

    visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        visibilityObserver?.disconnect();
        visibilityObserver = null;
        scheduleRender();
      });
    }, {
      rootMargin: "400px 0px"
    });

    visibilityObserver.observe(root);
  }

  function init() {
    document.addEventListener("cart:change", (event) => {
      const cart = event.detail?.cart;
      if (cart && typeof cart.item_count === "number") {
        lastKnownCartCount = cart.item_count;
      }
    });

    primeBundleProducts();

    const root = getRoot();
    if (root) {
      boundRoot = root;
      observeRootContent(root);
      startRenderWhenVisible(root);
    }

    watchForRoot();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
